import { useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatPKR, pkrToPaisa } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'
import { normalizeImei } from './purchasePayload'
import { buildDeviceUpdate, type DeviceEditForm } from './deviceUpdatePayload'
import { saveDeviceUpdate } from './saveDeviceUpdate'
import type { CarrierLockStatus, Device, DeviceImei, MdmStatus, PtaStatus, Purchase } from '@/offline/types'

const inputCls =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none'
const labelCls = 'mb-1 block text-xs font-medium text-slate-400'

const BRANDS = [
  { value: '', label: 'Brand (optional)' },
  { value: 'samsung', label: 'Samsung' },
  { value: 'oneplus', label: 'OnePlus' },
  { value: 'apple', label: 'Apple' },
  { value: 'google', label: 'Google Pixel' },
]

export function DeviceEditPage() {
  const { deviceId = '' } = useParams()
  const ws = getActiveWorkspaceId()

  const device = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).devices.get(deviceId) : undefined), [ws, deviceId])
  const imeis = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).device_imeis.where('device_id').equals(deviceId).toArray() : []),
    [ws, deviceId],
  )
  const purchase = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).purchases.where('device_id').equals(deviceId).first() : undefined),
    [ws, deviceId],
  )
  const categories = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).categories.where('workspace_id').equals(ws).filter((c) => !c.archived_at && !c.deleted_at).toArray() : []),
    [ws],
  )

  if (!ws || !device) return <Page title="Edit device" note="Loading…" />

  return (
    <EditForm
      ws={ws}
      device={device}
      imeis={imeis ?? []}
      purchase={purchase}
      categories={categories ?? []}
    />
  )
}

function EditForm({
  ws,
  device,
  imeis,
  purchase,
  categories,
}: {
  ws: string
  device: Device
  imeis: DeviceImei[]
  purchase?: Purchase
  categories: Array<{ id: string; name: string }>
}) {
  const navigate = useNavigate()
  const sortedImeis = [...imeis].sort((a, b) => a.slot - b.slot)

  const [model, setModel] = useState(device.model)
  const [categoryId, setCategoryId] = useState(device.category_id)
  const [brand, setBrand] = useState(device.brand_code ?? '')
  const [storage, setStorage] = useState(device.storage ?? '')
  const [ram, setRam] = useState(device.ram ?? '')
  const [condition, setCondition] = useState(device.condition ?? '')
  const [color, setColor] = useState(device.color ?? '')
  const [pta, setPta] = useState<PtaStatus>(device.pta_status)
  const [carrier, setCarrier] = useState<CarrierLockStatus>(device.carrier_lock_status ?? 'unknown')
  const [mdm, setMdm] = useState<MdmStatus>(device.mdm_status ?? 'not_observed')
  const [imei1, setImei1] = useState(sortedImeis[0]?.imei_normalized ?? '')
  const [imei2, setImei2] = useState(sortedImeis[1]?.imei_normalized ?? '')
  const [cost, setCost] = useState(String((purchase?.cost_paisa ?? 0) / 100))
  const [direct, setDirect] = useState(String((purchase?.direct_cost_paisa ?? 0) / 100))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isApple = brand === 'apple'

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!model.trim()) return setError('Model is required.')
    let costPaisa: number
    let directPaisa = 0
    try {
      costPaisa = pkrToPaisa(cost)
    } catch {
      return setError('Invalid cost.')
    }
    if (direct.trim()) {
      try {
        directPaisa = pkrToPaisa(direct)
      } catch {
        return setError('Invalid direct cost.')
      }
    }
    const n1 = normalizeImei(imei1)
    const n2 = normalizeImei(imei2)
    for (const [label, n] of [
      ['IMEI 1', n1],
      ['IMEI 2', n2],
    ] as const) {
      if (n && (n.length < 14 || n.length > 17)) return setError(`${label} must be 14-17 digits.`)
    }

    const formImeis = [
      sortedImeis[0] ? { id: sortedImeis[0].id, imei_normalized: n1, imei_pta_status: sortedImeis[0].imei_pta_status } : null,
      sortedImeis[1] ? { id: sortedImeis[1].id, imei_normalized: n2, imei_pta_status: sortedImeis[1].imei_pta_status } : null,
    ].filter((x): x is { id: string; imei_normalized: string; imei_pta_status: PtaStatus } => x !== null)

    const form: DeviceEditForm = {
      model,
      category_id: categoryId,
      brand_code: brand || null,
      storage: storage || null,
      ram: ram || null,
      condition: condition || null,
      color: color || null,
      pta_status: pta,
      carrier_lock_status: isApple ? carrier : null,
      mdm_status: isApple ? mdm : null,
      imeis: formImeis,
      cost_paisa: costPaisa,
      direct_cost_paisa: directPaisa,
    }

    const built = buildDeviceUpdate({ workspace_id: ws, device, imeis, purchase, form })
    setBusy(true)
    try {
      const summary = await saveDeviceUpdate(ws, built)
      if (summary.conflicts.length > 0) {
        setError('Conflict: another device has a newer version on the server. Your local edit is kept; reload and re-apply.')
        return
      }
      if (summary.rejected.length > 0) {
        const fe = summary.rejected[0].result.field_errors?.[0]
        setError(`Not saved: ${summary.rejected[0].result.status}${fe ? ` — ${fe.message}` : ''}`)
        return
      }
      navigate(`/inventory/${device.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page title={`Edit — ${device.model}`} note="Same device UUID; revision bumps and the change is audited.">
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="e-model">Model *</label>
            <input id="e-model" className={inputCls} value={model} onChange={(e) => setModel(e.target.value)} />
          </div>
          <div>
            <label className={labelCls} htmlFor="e-cat">Category</label>
            <select id="e-cat" className={inputCls} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="e-brand">Brand</label>
            <select id="e-brand" className={inputCls} value={brand} onChange={(e) => setBrand(e.target.value)}>
              {BRANDS.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls} htmlFor="e-pta">PTA</label>
            <select id="e-pta" className={inputCls} value={pta} onChange={(e) => setPta(e.target.value as PtaStatus)}>
              <option value="unknown">Unknown</option>
              <option value="approved">PTA Approved</option>
              <option value="non_pta">Non PTA</option>
            </select>
          </div>
        </div>

        {isApple ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls} htmlFor="e-carrier">Carrier lock</label>
              <select id="e-carrier" className={inputCls} value={carrier} onChange={(e) => setCarrier(e.target.value as CarrierLockStatus)}>
                <option value="unknown">Unknown</option>
                <option value="unlocked">Factory Unlock</option>
                <option value="carrier_locked">JV / Locked</option>
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="e-mdm">MDM</label>
              <select id="e-mdm" className={inputCls} value={mdm} onChange={(e) => setMdm(e.target.value as MdmStatus)}>
                <option value="not_observed">No MDM observed</option>
                <option value="enrolled">MDM enrolled</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="e-imei1">IMEI 1</label>
            <input id="e-imei1" className={inputCls} inputMode="numeric" value={imei1} onChange={(e) => setImei1(e.target.value)} disabled={!sortedImeis[0]} />
          </div>
          <div>
            <label className={labelCls} htmlFor="e-imei2">IMEI 2 (optional)</label>
            <input id="e-imei2" className={inputCls} inputMode="numeric" value={imei2} onChange={(e) => setImei2(e.target.value)} disabled={!sortedImeis[1]} />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className={labelCls} htmlFor="e-storage">Storage</label>
            <input id="e-storage" className={inputCls} value={storage} onChange={(e) => setStorage(e.target.value)} placeholder="128GB" />
          </div>
          <div>
            <label className={labelCls} htmlFor="e-ram">RAM</label>
            <input id="e-ram" className={inputCls} value={ram} onChange={(e) => setRam(e.target.value)} placeholder="8GB" />
          </div>
          <div>
            <label className={labelCls} htmlFor="e-condition">Condition</label>
            <input id="e-condition" className={inputCls} value={condition} onChange={(e) => setCondition(e.target.value)} placeholder="A grade" />
          </div>
          <div>
            <label className={labelCls} htmlFor="e-color">Color</label>
            <input id="e-color" className={inputCls} value={color} onChange={(e) => setColor(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="e-cost">Cost (PKR)</label>
            <input id="e-cost" className={inputCls} inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />
          </div>
          <div>
            <label className={labelCls} htmlFor="e-direct">Extra cost on this phone (PKR)</label>
            <input id="e-direct" className={inputCls} inputMode="decimal" value={direct} onChange={(e) => setDirect(e.target.value)} />
            <p className="mt-1 text-[11px] leading-snug text-slate-500">
              Repair parts / PTA fee / box-accessories jo is phone par lage; cost mein jud kar profit nikalta hai.
            </p>
          </div>
        </div>

        {cost ? <p className="text-xs text-slate-400">Cost: {safeFmt(cost)}</p> : null}

        {error ? (
          <p role="alert" className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</p>
        ) : null}

        <button type="submit" disabled={busy} className="w-full rounded-xl bg-sky-500 px-4 py-3 text-base font-semibold text-white disabled:opacity-60">
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </form>
    </Page>
  )
}

function safeFmt(raw: string): string {
  try {
    return formatPKR(pkrToPaisa(raw))
  } catch {
    return '—'
  }
}
